/* eslint-disable */
// noinspection SpellCheckingInspection
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import './layout.css';
import '../pages/b2bPage/b2bPage.css';
import '../pages/homePage/StationListSearch.css';
import { useTranslation } from 'react-i18next';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { ThemeProvider } from 'styled-components';
import { Footer, FooterLink, GlobalStyles, LinksColor, NavBar, NavLink, Translate } from './globalStyles';
import { darkTheme, redesignedDarkTheme } from './darkTheme/Theme';
import { useLocalStorage } from '../hookServices/useLocalStorage';
import ReactGA, { event } from 'react-ga';
import { SVGLogo } from './svgComponents/SVGLogo';
import {
  useGetBalanceQuery,
  useGetActiveEnergyCardsQuery,
  useGetInProgressOrdersCountQuery,
  useGetHistoryDataQuery,
  useGetStationStatusQuery,
} from '../redux/api';
import { useGetClientId } from '../hookServices/clientId';
import { useMinWidth } from '../hookServices/useMinWidth';
import { getDisplayBalanceCents } from '../utils/displayBalanceCents';
import {
  getDisplayEnergyCardKwh,
  getDisplayEnergyCardUah,
  hasKwhEnergyCards,
  hasUahEnergyCards,
} from '../utils/displayEnergyCardKwh';
import { isEnergyCardInvoiceId } from '../hookServices/energyCard';

import { Icon } from '@ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import OfflineNotification from './OfflineNotification';
import Spinner from './loaders/globalSpinner/Spinner';
import {
  isCapacitorAndroid,
  isCapacitorIos,
  isIosTouchDevice,
  lockIosInputAutoZoom,
  bindIosVisualViewportLock,
} from '../utils/iosInputZoom';
import { isIphoneUserAgent } from '../utils/portraitOrientation';

export default function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isIosSafariBrowser, setIsIosSafariBrowser] = useState(false);
  const { clientId, isLoading: isClientIdLoading } = useGetClientId();
  const [searchParams] = useSearchParams();
  let stationNumber = searchParams.get('station');
  const isStartManyPage = pathname.startsWith('/startMany');
  const isStartPage = pathname.startsWith('/start') && !isStartManyPage;
  const isChargingPage = pathname.startsWith('/charging');
  const isHistoryPage = pathname.startsWith('/history');
  // Idle map/list used to poll 4 APIs every 7s and keep nginx 429 windows from recovering.
  const headerPollMs = isChargingPage || isStartPage || isHistoryPage ? 7000 : 60_000;

  //todo replace this hack by redux
  const { data: freshBalance } = useGetBalanceQuery(clientId, { skip: !clientId, pollingInterval: headerPollMs });
  const { data: activeEnergyCards } = useGetActiveEnergyCardsQuery(clientId, {
    skip: !clientId,
    pollingInterval: headerPollMs,
  });
  const { data: inProgressOrdersCount } = useGetInProgressOrdersCountQuery(clientId, {
    skip: !clientId,
    pollingInterval: headerPollMs,
  });

  // Poll history for prepaid leftover + to detect real EC charging sessions (header blink).
  const { data: historyData } = useGetHistoryDataQuery(clientId, {
    skip: !clientId,
    pollingInterval: headerPollMs,
  });

  let toggleStatus = !open ? 'toggle-icon' : 'open toggle-icon ';

  const toggleMenu = () => {
    setOpen(!open);
  };

  const closeMenu = () => {
    setOpen(false);
  };

  const lngs = {
    ua: { nativeName: 'Укр' },
    en: { nativeName: 'Eng' },
  };

  const { t, i18n } = useTranslation();
  const nativeIos = isCapacitorIos();

  const goToListSearch = () => {
    closeMenu();
    ReactGA.event({
      category: 'home',
      action: 'list search from burger',
    });
    const params = new URLSearchParams();
    if (stationNumber) {
      params.set('station', stationNumber);
    }
    params.set('focusSearch', '1');
    navigate(`/list?${params.toString()}`);
  };

  /** Wide-screen B2B flow graph: hide navbar + fixed back/refresh for a centered fullscreen graph */
  const isWideViewport = useMinWidth(992);
  const hideB2bFlowGraphChrome = pathname === '/b2b' && searchParams.get('graphView') === '1' && isWideViewport;
  const isMapHomePage = pathname === '/';

  const inProgressPlugNumber = useMemo(() => {
    if (!Array.isArray(historyData)) {
      return null;
    }
    const item = historyData.find(entry => entry?.state === 'IN_PROGRESS' || entry?.state === 'WAITING');
    return item?.plugNumber ?? null;
  }, [historyData]);

  const statusStationNumber = stationNumber || inProgressPlugNumber;
  const shouldPollStationStatus =
    Boolean(statusStationNumber) && (isChargingPage || isStartPage || Boolean(inProgressPlugNumber));

  // Poll live station metrics on every screen that shows header balance during a session.
  const { data: stationStatus } = useGetStationStatusQuery(
    { stationNumber: statusStationNumber },
    {
      skip: !shouldPollStationStatus,
      pollingInterval: isChargingPage ? 2000 : 7000,
    }
  );

  // Same leftover on all screens: wallet + (paid - spent).
  const displayBalanceCents = getDisplayBalanceCents(freshBalance, historyData, stationStatus, statusStationNumber);
  const displayBalanceUah = displayBalanceCents / 100;
  const liveEnergyCards = (activeEnergyCards || []).filter(c => !c.expired);
  const lastJob = stationStatus?.lastJob;
  const stationEcCharging =
    Boolean(lastJob) &&
    (lastJob.state === 'IN_PROGRESS' || lastJob.state === 'WAITING') &&
    isEnergyCardInvoiceId(lastJob.invoiceId);
  const historyEcCharging = useMemo(() => {
    if (!Array.isArray(historyData)) {
      return false;
    }
    return historyData.some(
      item => (item?.state === 'IN_PROGRESS' || item?.state === 'WAITING') && isEnergyCardInvoiceId(item?.invoiceId)
    );
  }, [historyData]);
  // Only blink kWh while an Energy Card session is actually in progress (not merely because
  // active cards exist, and not when the API incorrectly flags every card).
  const ecCharging = stationEcCharging || historyEcCharging;
  // Energy-card sessions blink only kWh; wallet sessions keep UAH blink.
  const walletCharging = Boolean(inProgressOrdersCount > 0) && !ecCharging;
  // EC header: kWh/UAH left = balance - charged (same formula on every screen).
  const showEcKwh = hasKwhEnergyCards(liveEnergyCards);
  const showEcUah = hasUahEnergyCards(liveEnergyCards);
  const displayEnergyCardKwh = getDisplayEnergyCardKwh(liveEnergyCards, stationStatus, {
    ecCharging,
    useLiveCharged: Boolean(stationStatus),
    historyData,
  });
  const displayEnergyCardUah = getDisplayEnergyCardUah(liveEnergyCards, stationStatus, {
    ecCharging,
    useLiveCharged: Boolean(stationStatus),
    historyData,
    centsPerKwh: stationStatus?.costPerKwt,
  });
  const balanceUahRounded = Math.round(Number(displayBalanceUah));
  const energyCardKwhLabel = Number(displayEnergyCardKwh).toFixed(1);
  const energyCardUahLabel = Number(displayEnergyCardUah).toFixed(0);
  const headerBalanceValuesRef = useRef(null);
  const [headerBalanceCarousel, setHeaderBalanceCarousel] = useState(false);

  const measureHeaderBalanceOverflow = useCallback(() => {
    if (liveEnergyCards.length === 0) {
      setHeaderBalanceCarousel(false);
      return;
    }
    // Compact header slot: long UAH/kWh values switch to a 2-slide carousel.
    const longUah = String(balanceUahRounded).length >= 4;
    const longKwh = showEcKwh && energyCardKwhLabel.length >= 6;
    const longEcUah = showEcUah && energyCardUahLabel.length >= 4;
    const combinedTight =
      `${balanceUahRounded}${showEcKwh ? energyCardKwhLabel : ''}${showEcUah ? energyCardUahLabel : ''}`.length >= 7;
    setHeaderBalanceCarousel(longUah || longKwh || longEcUah || combinedTight);
  }, [balanceUahRounded, energyCardKwhLabel, energyCardUahLabel, liveEnergyCards.length, showEcKwh, showEcUah]);

  useEffect(() => {
    measureHeaderBalanceOverflow();
    const el = headerBalanceValuesRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => measureHeaderBalanceOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureHeaderBalanceOverflow]);

  // eslint-disable-next-line
  const [togglerStatus, setTogglerStatus] = useLocalStorage('themeTogglerStatus', false);
  const [theme, setTheme] = useLocalStorage('themeMode', 'light');
  // const [togglerStatus] = useLocalStorage('themeTogglerStatus', false);
  // const [, setTheme] = useLocalStorage('themeMode', 'light');
  const hasCharging = true;

  const darkModeSetter = () => {
    setTheme('dark');
  };
  const lightModeSetter = () => {
    setTheme('light');
  };

  const themeToggler = () => {
    setTogglerStatus(true);
    if (theme === 'light') {
      darkModeSetter();
    } else {
      lightModeSetter();
    }
  };

  useEffect(() => {
    if (togglerStatus === false) {
      if (stationStatus?.uiNightMode === false) {
        lightModeSetter();
      } else if (stationStatus?.uiNightMode === true) {
        darkModeSetter();
      }
    }
    // eslint-disable-next-line
  }, [stationStatus?.uiNightMode]);

  function isIos() {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      // iPad on isIos 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  /* Safari on iOS (browser): move navbar up. Capacitor WebView: keep current offset. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cap = window.Capacitor;
    const isCapacitor = cap != null && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform();
    const safari = isIos() && !isCapacitor;
    if (safari) {
      document.body.classList.add('ios-safari-browser');
      setIsIosSafariBrowser(true);
    }
    return () => {
      document.body.classList.remove('ios-safari-browser');
      setIsIosSafariBrowser(false);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const className = 'map-home-page';
    if (pathname === '/') {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => document.body.classList.remove(className);
  }, [pathname]);

  /* Native Capacitor: platform body class. iOS also locks input auto-zoom. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const capacitorIos = isCapacitorIos();
    const capacitorAndroid = isCapacitorAndroid();
    const iosDevice = isIosTouchDevice();
    if (!capacitorIos && !capacitorAndroid && !iosDevice) return;

    if (capacitorIos) {
      document.body.classList.add('capacitor-native-ios');
    }
    if (capacitorAndroid) {
      document.body.classList.add('capacitor-native-android');
    }

    // Native iOS: keep maximum-scale=1 for the whole session. Re-applying the
    // viewport meta on every search focus can freeze a mid-zoom scale and break
    // fixed chrome on every page after that.
    const restoreViewport = capacitorIos || iosDevice ? lockIosInputAutoZoom({ permanent: capacitorIos }) : () => {};
    const restoreVvLock = capacitorIos || iosDevice ? bindIosVisualViewportLock() : () => {};

    return () => {
      if (capacitorIos) {
        document.body.classList.remove('capacitor-native-ios');
      }
      if (capacitorAndroid) {
        document.body.classList.remove('capacitor-native-android');
      }
      restoreViewport();
      restoreVvLock();
    };
  }, []);

  /* Capacitor iPhone: reload after rotation so header layout re-initializes.
   * iPad and other big displays rotate without a reload. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cap = window.Capacitor;
    const isCapacitorIos =
      cap != null &&
      typeof cap.isNativePlatform === 'function' &&
      cap.isNativePlatform() &&
      typeof cap.getPlatform === 'function' &&
      cap.getPlatform() === 'ios';
    if (!isCapacitorIos || !isIphoneUserAgent(navigator.userAgent)) return;

    let reloadTimeoutId = null;
    let reloadRafId = null;

    const clearRotationReload = () => {
      if (reloadTimeoutId != null) {
        window.clearTimeout(reloadTimeoutId);
        reloadTimeoutId = null;
      }
      if (reloadRafId != null) {
        window.cancelAnimationFrame(reloadRafId);
        reloadRafId = null;
      }
    };

    const isRotationApplied = () => {
      const portrait = window.matchMedia('(orientation: portrait)').matches;
      return portrait ? window.innerHeight >= window.innerWidth : window.innerWidth > window.innerHeight;
    };

    const scheduleRotationReload = () => {
      clearRotationReload();

      const waitForRotationApplied = (attempt = 0) => {
        if (isRotationApplied()) {
          reloadTimeoutId = window.setTimeout(() => {
            reloadTimeoutId = null;
            if (isRotationApplied()) {
              window.location.reload();
            }
          }, 1000);
          return;
        }

        if (attempt < 30) {
          reloadRafId = window.requestAnimationFrame(() => waitForRotationApplied(attempt + 1));
        }
      };

      waitForRotationApplied();
    };

    window.addEventListener('orientationchange', scheduleRotationReload);
    const portraitMq = window.matchMedia('(orientation: portrait)');
    portraitMq.addEventListener('change', scheduleRotationReload);

    return () => {
      clearRotationReload();
      window.removeEventListener('orientationchange', scheduleRotationReload);
      portraitMq.removeEventListener('change', scheduleRotationReload);
    };
  }, []);

  return (
    // todo implement theme changes
    // <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
    <ThemeProvider theme={redesignedDarkTheme}>
      <>
        {/* Blocking loader while clientId is being generated */}
        {isClientIdLoading && (
          <div className="b2b-client-id-loader-overlay">
            <Spinner />
          </div>
        )}
        <OfflineNotification />
        <GlobalStyles />
        {!hideB2bFlowGraphChrome && (
          <NavBar id="layoutNavBar" className="fixed-top" expand={false} collapseOnSelect expanded={open}>
            <div className="nav-conteiner">
              <Navbar.Toggle
                id="layoutBurgerBtn"
                onClick={toggleMenu}
                bsPrefix={`${toggleStatus}`}
                style={{ position: 'absolute' }}
              />
              <LinksColor
                id="layoutLogoLink"
                onClick={() => {
                  ReactGA.event({
                    category: 'logo',
                    action: 'landing from logo click',
                  });
                  closeMenu();
                }}
                to={pathname === '/' ? `list/?station=${stationNumber}` : `/?station=${stationNumber}`}
              >
                <div id="layoutLogoImgBox" className="logoContainer">
                  <SVGLogo />
                </div>

                {/* <h3 id="layoutLogoText" className="ml-1 mb-0 logoText">
                220-km.com
              </h3> */}
              </LinksColor>
            </div>

            <div
              id="layoutHeaderCollapsedMenu"
              className={`collapsed-menu navbar-collapse collapse${open ? ' show' : ''}`}
            >
              <Nav id="layoutHeaderMenu" className="ml-auto align-items-center collapsed-menu-content">
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'qrScanner',
                        action: 'qr scanner page',
                      });
                    }}
                    eventKey="scanner"
                    as={Link}
                    to={`/scanner?station=${stationNumber}`}
                  >
                    <NavLink>1. {t('qrScanner.nameButton')}</NavLink>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'home',
                        action: 'landing from burger',
                      });
                    }}
                    eventKey="1"
                    as={Link}
                    to={stationNumber === null ? '/' : `/?station=${stationNumber}`}
                  >
                    <NavLink>2. {t('landingLinkMap')}</NavLink>
                  </Nav.Link>
                </Nav.Item>
                {clientId && (
                  <Nav.Item>
                    <Nav.Link
                      className="nav-link links"
                      onClick={() => {
                        closeMenu();
                        ReactGA.event({
                          category: 'history',
                          action: 'history page',
                        });
                      }}
                      eventKey="4"
                      as={Link}
                      to={`/history?station=${stationNumber}`}
                    >
                      <NavLink>3. {t('history')}</NavLink>
                    </Nav.Link>
                  </Nav.Item>
                )}
                <Nav.Item>
                  <Nav.Link
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'contacts',
                        action: 'contacts page',
                      });
                    }}
                    eventKey="3"
                    as={Link}
                    to={`/contacts?station=${stationNumber}`}
                  >
                    <NavLink>
                      {clientId ? '4. ' : '3. '}
                      {t('contacts')}
                    </NavLink>
                  </Nav.Link>
                </Nav.Item>
                {/*todo show charging session based on client ui id.*/}
                {/*{stationNumber !== "null" && stationNumber !== null && (*/}
                {/*  <Nav.Item>*/}
                {/*    <Nav.Link*/}
                {/*      style={{ textDecoration: "none" }}*/}
                {/*      className="nav-link links"*/}
                {/*      onClick={() => {*/}
                {/*        closeMenu();*/}
                {/*        ReactGA.event({*/}
                {/*          category: "charge",*/}
                {/*          action: "start or charging page",*/}
                {/*        });*/}
                {/*      }}*/}
                {/*      eventKey="2"*/}
                {/*      as={Link}*/}
                {/*      to={`/start?station=${stationNumber}`}*/}
                {/*    >*/}
                {/*      <NavLink>{t("chargeLink")}</NavLink>*/}
                {/*    </Nav.Link>*/}
                {/*  </Nav.Item>*/}
                {/*)}*/}
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'terms',
                        action: 'terms page',
                      });
                    }}
                    eventKey="5"
                    as={Link}
                    to={{ pathname: '/contract', search: '' }}
                  >
                    <NavLink>
                      {clientId ? '5. ' : '4. '}
                      {t('offer')}
                    </NavLink>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'b2b',
                        action: 'b2b page',
                      });
                    }}
                    eventKey="6"
                    as={Link}
                    to="/b2b"
                  >
                    <NavLink>
                      {clientId ? '6. ' : '5. '}
                      {t('b2bHeader')}
                    </NavLink>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'bess',
                        action: 'buy ups',
                      });
                    }}
                    eventKey="order-bess-link"
                    as="a"
                    href="https://220-km.com:9220/order-bess"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <NavLink>
                      {clientId ? '7. ' : '6. '}
                      {t('buyUps')}
                    </NavLink>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    style={{ textDecoration: 'none' }}
                    className="nav-link links"
                    onClick={() => {
                      closeMenu();
                      ReactGA.event({
                        category: 'installApp',
                        action: 'install app page',
                      });
                    }}
                    eventKey="install-app"
                    as={Link}
                    to={`/install-app?station=${stationNumber}`}
                  >
                    <NavLink>
                      {clientId ? '8. ' : '7. '}
                      {t('installApp')}
                    </NavLink>
                  </Nav.Link>
                </Nav.Item>
                {!nativeIos && (
                  <Nav.Item className="layout-burger-search-item">
                    <div className="station-list-search-wrap layout-burger-search-wrap">
                      <input
                        type="text"
                        inputMode="search"
                        readOnly
                        className="station-list-search-input layout-burger-search-input"
                        placeholder={t('stationListSearchPlaceholder')}
                        aria-label={t('stationListSearchPlaceholder')}
                        onClick={() => goToListSearch()}
                        onFocus={event => {
                          event.target.blur();
                          goToListSearch();
                        }}
                      />
                    </div>
                  </Nav.Item>
                )}
                <Nav.Item id="layoutNavBarChangeLangBox">
                  <Nav.Link
                    id="layoutNavBarChangeLangNavLink"
                    className="text-decoration-none"
                    eventKey="8"
                    onClick={event => {
                      event.stopPropagation();
                      ReactGA.event({
                        category: 'lang',
                        action: 'change language',
                      });
                    }}
                  >
                    {Object.keys(lngs).map((lng, i) => (
                      <div key={lng}>
                        <Translate
                          id={`layoutNavBar${lngs[lng].nativeName}Btn`}
                          className={i18n.resolvedLanguage === lng ? 'btnLang active' : 'btnLang'}
                          key={lng}
                          type="submit"
                          onClick={() => {
                            i18n.changeLanguage(lng);
                            closeMenu();
                          }}
                        >
                          {lngs[lng].nativeName}
                        </Translate>
                        {i < Object.keys(lngs).length - 1 && <Icon i="lngEarth" size="auto" className="leng-icon" />}
                      </div>
                    ))}
                  </Nav.Link>
                </Nav.Item>

                {/*<Nav.Item id="layoutNavBarChangeThemeBox">*/}
                {/*  <Nav.Link id="layoutNavBarChangeThemeNavLink" eventKey="8" onClick={closeMenu}>*/}
                {/*    <button*/}
                {/*      id="layoutNavBarChangeThemeBtn"*/}
                {/*      style={{*/}
                {/*        backgroundColor: 'transparent',*/}
                {/*        border: 'none',*/}
                {/*        marginTop: '2px',*/}
                {/*      }}*/}
                {/*      onClick={() => {*/}
                {/*        themeToggler();*/}
                {/*        ReactGA.event({*/}
                {/*          category: 'theme',*/}
                {/*          action: 'change theme',*/}
                {/*        });*/}
                {/*      }}*/}
                {/*    >*/}
                {/*      {theme === 'light' ? <SVGNightMode /> : <SVGLightMode />}*/}
                {/*    </button>*/}
                {/*  </Nav.Link>*/}
                {/*</Nav.Item>*/}
              </Nav>
            </div>

            <div
              className="balance-container header-balances-wrap"
              role="button"
              tabIndex={0}
              title={
                liveEnergyCards.length
                  ? liveEnergyCards
                      .flatMap(c => c.stationNumbers || [])
                      .map(n => `#${n}`)
                      .join(', ')
                  : undefined
              }
              onClick={() => {
                ReactGA.event({
                  category: 'header',
                  action: 'history form header',
                });
                if (hasCharging) {
                  navigate('/history');
                }
                closeMenu();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }}
            >
              <span className={`textBalance${walletCharging ? ' textBalanceCharging' : ''}`}>
                <span className="textBalanceLabel">
                  <span className="textBalanceLabelNe">{t('balanceNe')}</span>
                  {t('balanceWord')}
                </span>
                <span
                  ref={headerBalanceValuesRef}
                  className={[
                    walletCharging ? 'headerBalanceValues blink_me' : 'headerBalanceValues',
                    headerBalanceCarousel ? 'headerBalanceValues--carousel' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="headerBalanceSlide headerBalanceSegment headerBalanceSegment--uah">
                    <span className="textBalanceAmount">{balanceUahRounded}</span>
                    <span className="textBalanceCurrency">₴</span>
                  </span>
                  {showEcKwh && (
                    <>
                      {!headerBalanceCarousel && (
                        <span className="headerBalanceSep" aria-hidden="true">
                          |
                        </span>
                      )}
                      <span
                        className={[
                          'headerBalanceSlide',
                          'headerBalanceSegment',
                          'headerBalanceSegment--kwh',
                          ecCharging ? 'headerBalanceKwhCharging' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span className="headerBalanceValue">{energyCardKwhLabel}</span>
                        <span className="headerBalanceUnit">{t('wt')}</span>
                      </span>
                    </>
                  )}
                  {showEcUah && (
                    <>
                      {!headerBalanceCarousel && (
                        <span className="headerBalanceSep" aria-hidden="true">
                          |
                        </span>
                      )}
                      <span
                        className={[
                          'headerBalanceSlide',
                          'headerBalanceSegment',
                          'headerBalanceSegment--ec-uah',
                          ecCharging ? 'headerBalanceKwhCharging' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span className="headerBalanceValue">{energyCardUahLabel}</span>
                        <span className="headerBalanceUnit">{t('currency').trim()}</span>
                      </span>
                    </>
                  )}
                </span>
              </span>
            </div>
          </NavBar>
        )}

        <Container
          id="layout"
          fluid
          style={{
            ...{
              fontFamily: 'var(--font-family)',
              fontWeight: '500',
              fontSize: '18px',
              color: '#fff',
              letterSpacing: '0.15em',
            },
            // height: '100vh',
          }}
          className="justify-content-center ml-0 pl-0 mr-0 pr-0"
        >
          {/* Back button under burger menu (left); Refresh button in top-right corner */}
          {pathname !== '/' && !open && !hideB2bFlowGraphChrome && (
            <>
              <div className="layout-page-chrome-wrap layout-page-chrome-wrap--left">
                <button
                  type="button"
                  className="layout-page-chrome-btn"
                  onClick={() => {
                    if (pathname.startsWith('/charging')) {
                      navigate(-2);
                    } else {
                      navigate(-1);
                    }
                  }}
                >
                  <ArrowBackIcon className="layout-page-chrome-btn__icon" />
                </button>
              </div>
              <div className="layout-page-chrome-wrap layout-page-chrome-wrap--right">
                <button
                  type="button"
                  className="layout-page-chrome-btn"
                  onClick={() => window.location.reload()}
                  aria-label={t('refresh')}
                >
                  <RefreshIcon className="layout-page-chrome-btn__icon" />
                </button>
              </div>
            </>
          )}

          <div>
            <div
              className="nav-overlay"
              style={{
                display: open ? 'block' : 'none',
              }}
              onClick={closeMenu}
            ></div>
            {/* Scrollable main content: only this area scrolls, so navbar never moves on overscroll */}
            <div
              id="layoutMainScroll"
              style={{
                height: isMapHomePage ? 'var(--map-viewport-height, 100dvh)' : '100vh',
                overflowY: isMapHomePage ? 'hidden' : 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorY: 'none',
                paddingBottom: isMapHomePage
                  ? 0
                  : pathname.startsWith('/start') ||
                      pathname.startsWith('/charging') ||
                      pathname.startsWith('/scanner') ||
                      pathname.startsWith('/install-app')
                    ? 'calc(80px + env(safe-area-inset-bottom, 0px))'
                    : '80px',
              }}
            >
              {!isClientIdLoading && <Outlet />}
            </div>
          </div>
        </Container>
      </>
    </ThemeProvider>
  );
}
